import classNames from 'classnames'
import { useCallback, useMemo } from 'react'
import { useDropdownPositioning } from '@opallabs/hooks'
import { Portal } from '../../../Portal'
import { singleSelectClasses } from '../componentClassNames'
import { useSingleSelectState } from '../contexts'
import * as styles from './styles'

/**
 * The design's menu is 220px wide (Web Components 3344:6362). A menu under a
 * wider trigger is as wide as the trigger; a menu under a narrower one is this.
 */
const DEFAULT_MENU_WIDTH = 220

type MenuProps = {
  /**
   * Which edge of the trigger the menu lines up with. `start` by default; a
   * trigger at the end of its row asks for `end`, so the menu hangs inward.
   */
  align?: 'start' | 'end'
  className?: string
  menuWidth?: string | number
  /**
   * How wide the menu may grow past its trigger, in pixels: 220 by default,
   * the width of the design's menu. The positioning clamps a menu to this or
   * its trigger's width, whichever is wider, so a menu given a `menuWidth`
   * past it asks for the room here too.
   */
  minContentWidth?: number
  portalled?: boolean
}

export const Menu: React.FC<React.PropsWithChildren<MenuProps>> = ({
  align = 'start',
  children,
  className,
  menuWidth = DEFAULT_MENU_WIDTH,
  minContentWidth = DEFAULT_MENU_WIDTH,
  portalled,
}) => {
  const {
    componentId,
    isOpen,
    menuContainerRef,
    optionCount,
    setMenuContainerRef,
    toggleRef,
  } = useSingleSelectState()

  const positioning = useMemo(
    () => ({
      computePosition: { placement: `bottom-${align}` as const },
      minContentWidth,
    }),
    [align, minContentWidth]
  )

  // Apply smart positioning to dropdown menu
  const { isPositioned } = useDropdownPositioning(
    toggleRef.current,
    menuContainerRef.current,
    positioning
  )

  const handleMenuContainerRefChange = useCallback(
    (node: HTMLDivElement | null) => setMenuContainerRef({ current: node }),
    [setMenuContainerRef]
  )

  return (
    <Portal enabled={portalled && isOpen}>
      <div
        className={classNames(singleSelectClasses.menu, className)}
        style={{
          width: menuWidth,
          display: !isOpen ? 'none' : undefined,
          visibility: !isPositioned ? 'hidden' : undefined,
        }}
        id={`${componentId}-menu-container`}
        ref={handleMenuContainerRefChange}
        css={styles.menu({
          optionCount,
        })}
      >
        {children}
      </div>
    </Portal>
  )
}
Menu.displayName = 'Lapidary: Menu ← SingleSelect'
